import {
	faChevronLeft,
	faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { PortalStorefrontItemCardDto } from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StorefrontItemCard } from './StorefrontItemCard';

/**
 * Horizontally-scrolling item card carousel — Amazon / Weee homepage
 * pattern. Native CSS scroll + scroll-snap so it works without a JS
 * library; chevron buttons advance one viewport-width at a time on
 * desktop, hidden on mobile (touch users swipe).
 *
 * Each card mirrors the catalog grid card: tap navigates to the
 * product detail page, an in-image floating "+" / stepper drives
 * cart actions without leaving the dashboard, and a promo headline
 * (when one applies) renders a discount badge at the top-left + a
 * struck-through-with-promo-price treatment in the price slot. The
 * earlier carousel deliberately omitted these to stay lightweight,
 * but customers reading the dashboard couldn't see which items were
 * on sale or queue them up — they had to navigate into /catalog or
 * the product detail page first, which broke the "shop the sale
 * from the homepage" intent of the promo-with-items widget.
 *
 * Used by the Reorder, Popular, items-by-category, and
 * promo-with-items widgets.
 */
export function ItemCarousel({
	items,
	emptyState,
	invalidateQueryKey,
	density = 'standard',
}: {
	items: Array<PortalStorefrontItemCardDto>;
	emptyState: React.ReactNode;
	/**
	 * Query key whose payload produced these items' presigned image URLs.
	 * On a stale-image failure we invalidate it so the caller refetches
	 * with freshly-signed URLs. Optional — surfaces that don't have a
	 * single owning query (or don't need self-heal) can omit it.
	 */
	invalidateQueryKey?: ReadonlyArray<unknown>;
	/**
	 * Density preset. `compact` shrinks per-card width on mobile so more
	 * items fit per viewport (~25–30% denser). Desktop unchanged. Set
	 * per widget instance via `widgetConfig.density`.
	 */
	density?: 'standard' | 'compact';
}): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const scrollerRef = useRef<HTMLDivElement | null>(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	// Update arrow visibility based on scroll position. We watch the
	// scroller element rather than the items list because the visual
	// availability of arrows depends on actual layout (viewport width
	// vs total content width), not just count.
	const updateArrows = useCallback(() => {
		const el = scrollerRef.current;
		if (el == null) return;
		setCanScrollLeft(el.scrollLeft > 4);
		setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
	}, []);

	useEffect(() => {
		updateArrows();
		const el = scrollerRef.current;
		if (el == null) return;
		el.addEventListener('scroll', updateArrows, { passive: true });
		window.addEventListener('resize', updateArrows);
		return () => {
			el.removeEventListener('scroll', updateArrows);
			window.removeEventListener('resize', updateArrows);
		};
	}, [updateArrows, items.length]);

	const scrollByViewport = (direction: 1 | -1) => {
		const el = scrollerRef.current;
		if (el == null) return;
		// Page-size slightly less than full viewport so the next "page"
		// retains a partial card on the leading edge as a hint that
		// there's more content. Matches Amazon's carousel behaviour.
		const delta = el.clientWidth * 0.85 * direction;
		el.scrollBy({ left: delta, behavior: 'smooth' });
	};

	if (items.length === 0) {
		return <>{emptyState}</>;
	}

	return (
		<div className="relative w-full h-full">
			<div
				ref={scrollerRef}
				className="flex gap-3 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 -mx-1 px-1 scrollbar-thin"
				style={{ scrollbarWidth: 'thin' }}
			>
				{items.map((item) => (
					<div
						key={item.id}
						className={`snap-start shrink-0 ${
							density === 'compact'
								? 'w-[110px] sm:w-[140px] md:w-[180px]'
								: 'w-[150px] sm:w-[170px] md:w-[180px]'
						}`}
					>
						<StorefrontItemCard
							item={item}
							onNavigate={() => navigate(`/product/${item.id}`)}
							onLikelyStaleUrl={
								invalidateQueryKey != null
									? () => {
											void queryClient.invalidateQueries({
												queryKey: invalidateQueryKey,
											});
										}
									: undefined
							}
						/>
					</div>
				))}
			</div>

			{/* Desktop arrows — hidden when there's no overflow on either
			    side. Touch users swipe; the arrows are extra affordance. */}
			{canScrollLeft && (
				<button
					type="button"
					onClick={() => scrollByViewport(-1)}
					aria-label={t('Scroll left')}
					className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-8 h-8 items-center justify-center rounded-full bg-white shadow-lg border border-brand-border hover:bg-brand-input z-10"
				>
					<FontAwesomeIcon icon={faChevronLeft} />
				</button>
			)}
			{canScrollRight && (
				<button
					type="button"
					onClick={() => scrollByViewport(1)}
					aria-label={t('Scroll right')}
					className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-8 h-8 items-center justify-center rounded-full bg-white shadow-lg border border-brand-border hover:bg-brand-input z-10"
				>
					<FontAwesomeIcon icon={faChevronRight} />
				</button>
			)}
		</div>
	);
}
